import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  fetchCurrentPeriodLeaderboard,
  fetchLeaderboardStats,
  fetchTopUsersForRewards,
  selectCurrentPeriodData,
  selectCurrentPeriodError,
  selectCurrentPeriodLoading,
  selectCurrentPeriodPagination,
  selectStatsData,
  selectStatsLoading,
  selectTopUsersData,
} from "../../redux/slices/leaderboardSlice";
import AdminLeaderboardChrome from "../leaderboard/AdminLeaderboardChrome";
import LeaderboardPodium from "../leaderboard/LeaderboardPodium";
import LeaderboardResetClock from "../leaderboard/LeaderboardResetClock";
import LeaderboardRewardLadder from "../leaderboard/LeaderboardRewardLadder";
import LeaderboardStatsBand from "../leaderboard/LeaderboardStatsBand";
import LeaderboardRankStream from "../leaderboard/LeaderboardRankStream";
import LeaderboardHistoryPanel from "../leaderboard/LeaderboardHistoryPanel";
import { matchesLeaderSearch } from "../leaderboard/leaderboardHelpers";
import "../leaderboard/admin-leaderboard.css";

const PAGE_SIZE = 20;

export default function LeaderboardPage() {
  const dispatch = useDispatch();
  const rows = useSelector(selectCurrentPeriodData);
  const loading = useSelector(selectCurrentPeriodLoading);
  const error = useSelector(selectCurrentPeriodError);
  const pagination = useSelector(selectCurrentPeriodPagination);
  const stats = useSelector(selectStatsData);
  const statsLoading = useSelector(selectStatsLoading);
  const topTen = useSelector(selectTopUsersData);

  const [view, setView] = useState("live");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const loadLive = useCallback(
    (nextPage = 1) => {
      dispatch(fetchCurrentPeriodLeaderboard({ page: nextPage, limit: PAGE_SIZE }));
      dispatch(fetchLeaderboardStats());
      dispatch(fetchTopUsersForRewards({ limit: 10 }));
    },
    [dispatch]
  );

  useEffect(() => {
    if (view === "live") {
      loadLive(page);
    }
  }, [view, page, loadLive]);

  const filteredRows = useMemo(
    () => (rows || []).filter((row) => matchesLeaderSearch(row, search)),
    [rows, search]
  );

  const showPodium = page === 1 && !search;
  const topThree = showPodium ? filteredRows.slice(0, 3) : [];
  /* Admin desk: full page in scrollable stream (podium still highlights top 3). */
  const streamRows = filteredRows;

  const handleRefresh = () => {
    if (view === "live") {
      loadLive(page);
      return;
    }
    setHistoryRefresh((n) => n + 1);
  };

  return (
    <div className="alb-page">
      <AdminLeaderboardChrome
        view={view}
        onViewChange={setView}
        search={search}
        onSearchChange={setSearch}
        loading={loading}
        onRefresh={handleRefresh}
        totalRanked={pagination.totalUsers}
        page={pagination.currentPage}
        totalPages={pagination.totalPages}
      />

      {view === "history" ? (
        <LeaderboardHistoryPanel refreshToken={historyRefresh} />
      ) : (
        <>
          <LeaderboardStatsBand
            stats={stats}
            totalRanked={pagination.totalUsers}
            loading={statsLoading}
          />

          {error ? (
            <div className="alb-error" role="alert">
              {error}
              <button type="button" onClick={() => loadLive(page)}>
                Retry
              </button>
            </div>
          ) : null}

          {showPodium ? (
            <div className="alb-hero-grid">
              <LeaderboardResetClock />
              <LeaderboardPodium topThree={topThree} />
              <LeaderboardRewardLadder topTen={topTen} />
            </div>
          ) : (
            <div className="alb-hero-compact">
              <LeaderboardResetClock />
              <LeaderboardRewardLadder topTen={topTen} />
            </div>
          )}

          <section className="alb-stream-panel" aria-label="Rankings">
            <header className="alb-stream-head">
              <div>
                <strong>{showPodium ? "All rankings" : `Rankings · page ${page}`}</strong>
                <span>
                  {streamRows.length} member{streamRows.length === 1 ? "" : "s"}
                  {search ? ` matching “${search}”` : ""}
                </span>
              </div>
            </header>

            <LeaderboardRankStream
              rows={streamRows}
              loading={loading}
              emptyLabel={
                search
                  ? "No members match your search on this page."
                  : "No ranked members on this page."
              }
            />

            {pagination.totalPages > 1 && !search ? (
              <footer className="alb-pager">
                <button
                  type="button"
                  disabled={!pagination.hasPrev || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="alb-pager-meta tabular-nums">
                  {pagination.currentPage} / {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={!pagination.hasNext || loading}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
              </footer>
            ) : null}
          </section>
        </>
      )}
    </div>
  );
}
